import { create } from '@bufbuild/protobuf'
import type { ConnectError } from '@connectrpc/connect'
import { useQuery } from '@connectrpc/connect-query'
import { listBiddingSpotPendingApprovalDates } from '@ebalance/proto/bffuser/v1/bidding_spot_service-BiddingSpotService_connectquery'
import {
  ListBiddingSpotPendingApprovalDatesRequestSchema,
  type ListBiddingSpotPendingApprovalDatesResponse,
} from '@ebalance/proto/bffuser/v1/bidding_spot_service_pb'
import type { UseQueryResult } from '@tanstack/react-query'

import { APPROVAL_STATE_REFETCH_INTERVAL_MS } from '~/hooks/bidding/refetchInterval'
import { fromProtoDate } from '~/models/dateTime'

/** 1 受渡日の承認申請中 (PENDING) の申請数。 */
export type SpotPendingApprovalDate = {
  targetDate: Date
  requestCount: number
}

/**
 * 受渡日の欠けた要素は throw して query を error に倒す。 空扱いにすると
 * 「他の受渡日に承認待ちは無い」 と読める画面になり、 AC-BSP-095 が防ぐ見落としになる。
 */
export function convertPendingApprovalDates(
  resp: ListBiddingSpotPendingApprovalDatesResponse,
): SpotPendingApprovalDate[] {
  return resp.dates.map((d) => {
    if (d.targetDate === undefined) {
      throw new Error(
        'ListBiddingSpotPendingApprovalDates: target_date is missing',
      )
    }
    return {
      targetDate: fromProtoDate(d.targetDate),
      requestCount: d.requestCount,
    }
  })
}

/**
 * 入札締切前の受渡日ごとの承認申請中の申請数 (AC-BSP-095 の他日承認待ちバナー用)。
 *
 * 取得失敗は `throwOnError: false` で画面全体を落とさず、 呼び出し元が `isError` で
 * 知らせる ([[useListBiddingSpotApprovalRequests]] と同じ fail-safe)。 承認者の承認・
 * 差戻しを手動リロードなしで反映するため、 承認状態と同じ間隔で polling する (AC-BSP-096)。
 */
export function useListBiddingSpotPendingApprovalDates(): UseQueryResult<
  SpotPendingApprovalDate[],
  ConnectError
> {
  return useQuery(
    listBiddingSpotPendingApprovalDates,
    create(ListBiddingSpotPendingApprovalDatesRequestSchema, {}),
    {
      select: convertPendingApprovalDates,
      refetchInterval: APPROVAL_STATE_REFETCH_INTERVAL_MS,
      throwOnError: false,
    },
  )
}
